import { Box, styled } from '@mui/material';
import { format as originalFormat } from 'date-fns';
import { memo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';

import {
    GetGetFlightsResponse,
    KeyGetGetFlights,
    useMutationPutValidateResourcesAllocationFlights,
    useQueryGetGetFlights,
} from '../../../../backend';
import { errorToastConfig, successToastConfig } from '../../../../config/constants';
import { RoundButton } from '../../../Button/RoundButton/RoundButton';
import { useRmsGanttState, useRmsGanttValue } from '../RmsContext';
import { ganttDayFormat } from './gantt.constants';

const StyledAllocationControlBox = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 100%;
    height: 120px;
    background-color: ${({ theme }) => theme.palette.blue.dark};
`;
export const GanttAllocationControl = memo(function GanttAllocationControl() {
    const [nbUpdateAllocations, setNbUpdateAllocations] = useRmsGanttState('nbUpdateAllocations');
    const [proposedAllocation, setProposedAllocation] = useRmsGanttState('proposedAllocation');
    const resourceType = useRmsGanttValue('resourceType');
    const day = useRmsGanttValue('day');
    const { data: ganttDto } = useQueryGetGetFlights({
        selectedDay: originalFormat(day, ganttDayFormat),
        selectedResourceType: resourceType,
    });

    const queryClient = useQueryClient();

    const { mutateAsync: validateProposal, isLoading } = useMutationPutValidateResourcesAllocationFlights({
        onSuccess: response => {
            queryClient.setQueriesData(
                {
                    queryKey: [
                        KeyGetGetFlights,
                        {
                            selectedDay: originalFormat(day, ganttDayFormat),
                            selectedResourceType: resourceType,
                        },
                    ],
                },
                (values: GetGetFlightsResponse): GetGetFlightsResponse => ({
                    ...values,
                    data: response.data,
                }),
            );
            toast(<FormattedMessage id={'schedule.allocate.successToast'} />, successToastConfig);
            setProposedAllocation(null);
            setNbUpdateAllocations(0);
        },
        onError: () => {
            toast(<FormattedMessage id={'schedule.allocate.errorToast'} />, errorToastConfig);
        },
    });

    const handleCancel = useCallback(() => {
        setNbUpdateAllocations(0);
        setProposedAllocation(null);
    }, [setNbUpdateAllocations, setProposedAllocation]);

    const handleValidate = useCallback(() => {
        validateProposal({
            selectedDay: originalFormat(day, ganttDayFormat),
            selectedResourceType: resourceType,
            resourceAllocationValidationDto: {
                turnarounds: ganttDto?.turnarounds,
                proposedAllocation,
            },
        });
    }, [day, ganttDto?.turnarounds, proposedAllocation, resourceType, validateProposal]);

    if (nbUpdateAllocations === 0) {
        setProposedAllocation(null);
        return null;
    }

    return (
        <StyledAllocationControlBox>
            <Box display="flex" justifyContent="center" alignItems="space-between">
                <RoundButton color="primary" size="medium" variant="outlined" onClick={handleCancel}>
                    <FormattedMessage id="schedule.allocate.cancelControlButton" />
                </RoundButton>
                &emsp;
                <RoundButton
                    color="primary"
                    size="medium"
                    variant="contained"
                    onClick={handleValidate}
                    loading={isLoading}
                    disabled={isLoading || proposedAllocation.length === 0}
                >
                    <FormattedMessage
                        id="schedule.allocate.confirmControlButton"
                        values={{
                            count: nbUpdateAllocations,
                        }}
                    />
                </RoundButton>
            </Box>
        </StyledAllocationControlBox>
    );
});
